export class FormsConfigurationError extends Error {
  public readonly userMessage = 'Online forms are temporarily unavailable. Please call or email the salon.'

  constructor(message: string) {
    super(message)
    this.name = 'FormsConfigurationError'
  }
}

function joinUrl(baseUrl: string, endpoint: string) {
  return `${baseUrl.replace(/\/+$/, '')}/${endpoint.replace(/^\/+/, '')}`
}

const CONTACT_FORM_ENDPOINT = '/api/send-contact'
const INQUIRY_FORM_ENDPOINT = '/api/send-inquiry'

export type FormsApiConfig = {
  contactUrl: string
  inquiryUrl: string
  recaptchaSiteKey?: string
}

export function getFormsApiConfig(): FormsApiConfig {
  const baseUrl = import.meta.env.VITE_FORMS_API_BASE_URL?.trim()
  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY?.trim() || undefined

  if (!baseUrl) {
    throw new FormsConfigurationError(
      'VITE_FORMS_API_BASE_URL is required before forms can submit. Add it to a local .env file or the GitHub Pages build environment.',
    )
  }

  let parsedBaseUrl: URL
  try {
    parsedBaseUrl = new URL(baseUrl)
  } catch {
    throw new FormsConfigurationError('VITE_FORMS_API_BASE_URL must be a valid absolute URL.')
  }

  if (!['http:', 'https:'].includes(parsedBaseUrl.protocol)) {
    throw new FormsConfigurationError('VITE_FORMS_API_BASE_URL must use HTTP or HTTPS.')
  }

  if (import.meta.env.PROD && parsedBaseUrl.protocol !== 'https:') {
    throw new FormsConfigurationError('The production forms API must use HTTPS.')
  }

  if (!import.meta.env.PROD && parsedBaseUrl.protocol === 'http:' && !['localhost', '127.0.0.1', '[::1]'].includes(parsedBaseUrl.hostname)) {
    throw new FormsConfigurationError('An HTTP forms API is allowed only on localhost during development.')
  }

  if (parsedBaseUrl.username || parsedBaseUrl.password || parsedBaseUrl.pathname !== '/' || parsedBaseUrl.search || parsedBaseUrl.hash) {
    throw new FormsConfigurationError('VITE_FORMS_API_BASE_URL must contain only the Azure Function origin; configure form paths separately.')
  }

  return {
    contactUrl: joinUrl(parsedBaseUrl.toString(), CONTACT_FORM_ENDPOINT),
    inquiryUrl: joinUrl(parsedBaseUrl.toString(), INQUIRY_FORM_ENDPOINT),
    recaptchaSiteKey,
  }
}

export function getRecaptchaSiteKey() {
  return import.meta.env.VITE_RECAPTCHA_SITE_KEY?.trim() || undefined
}
